import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable, of, from } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { filter, first, map, shareReplay } from 'rxjs/operators';
import { ILanguage } from './language.model';
import { GET_OPTIONS_URL } from '../shared/api.utils';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

/**
 * Language service.
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private languages: Subject<any | void> = new Subject<any | void>();
  private loadedLanguagesSubject: Subject<any> = new Subject();

  public systemLanguage: Observable<string> = this.languages.asObservable();
  public loadedLanguages: Observable<string> = this.loadedLanguagesSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookiesService: CookieService,
    private http: HttpClient,
    private router: Router,
    private translateService: TranslateService
  ) {
    this.selectLocaleFromUrl();
  }

  /**
   * xGets languages.
   */
  public getLanguages(): Observable<ILanguage[]> {
    const locale = this.cookiesService.get('locale') || 'en';

    if (isPlatformBrowser(this.platformId)) {
      return this.http.get(GET_OPTIONS_URL('/dropdowns/languages', locale)).pipe(
        map(languages =>
          Object.entries(languages).map(([key, value]) => ({
            id: key,
            language: value,
            isSelected: false
          }))
        ),
        map(languages => languages.sort((a, b) => (a.language > b.language ? 1 : b.language > a.language ? -1 : 0))),
        shareReplay()
      );
    }

    return of([]);
  }

  /**
   * Sets language for site
   */
  public setLanguage(locale: string): void {
    !locale ? (locale = this.cookiesService.get('locale') || 'en') : this.cookiesService.set('locale', locale);

    if (this.translateService.currentLang !== locale) {
      this.translateService.use(locale);
    }

    this.translateService.setDefaultLang(locale);
    (this.languages as Subject<void>).next();
  }

  private selectLocaleFromUrl(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        first()
      )
      .subscribe(() => {
        const firstSegment = this.router.url.split('/')[1];
        const locale = firstSegment?.length === 2 ? firstSegment : 'en';

        if (locale) {
          this.setLanguage(locale);
          this.cookiesService.set('locale', locale);
        }

        if (isPlatformServer(this.platformId)) {
          console.log(`[Prerendering route]: '${this.router.url}' | [Locale]: '${locale}'`);
        }
      });
  }
}
